import { useEffect, useRef, useState } from 'react';
import { MessageSquareText, Send, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * OrbisChatBubble — non-functional preview of the future GenUI chat surface.
 *
 * The real conversational layer is Vijay's next-sprint work (Section 3 of
 * docs/WisdomX_GenUI_Brief.docx — `POST /api/v1/chat/query` + Anthropic
 * reasoning + dynamic panel rendering). Until that lands, this component
 * exists to signal the coming direction on the Home page: a floating
 * launcher in the bottom-right, expanding to a chat panel with the ORBIS
 * greeting, one suggested prompt from the brief, and a placeholder input.
 *
 * Nothing here submits or fetches. The suggested prompt fills the input on
 * click so a demo viewer sees the intended UX; the send button is a visual
 * cue only. The panel is decorated with a "Preview" chip so a screenshot of
 * this page never gets mistaken for a shipped chat feature.
 *
 * When Vijay ships the endpoint, swap `handleSuggest`/`handleSubmit` for real
 * fetch handlers, drop the Preview chip, and render the answer + panel below
 * the message list.
 */
const SUGGESTED_PROMPT =
  'Which systems in our estate are costing the most and carrying the highest security risk?';

export function OrbisChatBubble() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.querySelector('input')?.focus();
    else launcherRef.current?.focus();
  }, [open]);

  return (
    <div className="fixed bottom-5 right-5 z-40 lg:bottom-6 lg:right-6">
      {open ? (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Ask ORBIS — preview"
          className="flex h-[min(560px,calc(100vh-6rem))] w-[calc(100vw-2.5rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-[#1F2937] bg-[rgba(11,18,32,0.98)] shadow-2xl backdrop-blur"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-3 border-b border-[#1F2937] px-4 py-3">
            <div className="flex items-center gap-2.5">
              <div className="flex size-8 items-center justify-center rounded-full bg-brand-orange-500 text-sm font-bold text-white">
                O
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-white">Ask ORBIS</span>
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-emerald-400">
                  <span
                    aria-hidden="true"
                    className="size-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]"
                  />
                  Connected
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="flex size-7 shrink-0 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body — greeting + suggested prompt. flex-1 pushes the input to
              the bottom of the taller panel; overflow-y-auto so long threads
              scroll once the real chat is wired. */}
          <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
            <button
              type="button"
              onClick={() => setDraft(SUGGESTED_PROMPT)}
              className={cn(
                'self-start rounded-full border border-brand-orange-500/50 bg-brand-orange-500/10',
                'px-3 py-1.5 text-left text-[13px] text-orange-100 transition-colors',
                'hover:border-brand-orange-500 hover:bg-brand-orange-500/20 hover:text-white',
              )}
            >
              {SUGGESTED_PROMPT}
            </button>
          </div>

          {/* Input — decorative, does not submit */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex items-center gap-2 border-t border-[#1F2937] px-3 py-3"
          >
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type here..."
              aria-label="Ask ORBIS (preview — not yet wired)"
              className={cn(
                'w-full rounded-full border border-white/10 bg-white/[0.04]',
                'px-4 py-2 text-sm text-white placeholder:text-white/40',
                'focus:border-brand-orange-500/50 focus:outline-none',
              )}
            />
            <button
              type="submit"
              disabled
              aria-label="Send (preview — not wired)"
              title="Chat is a preview — the real endpoint ships next sprint"
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-orange-500 text-white opacity-70"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      ) : (
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open ORBIS chat preview"
          className={cn(
            'group relative flex size-14 items-center justify-center rounded-full bg-brand-orange-500 text-white shadow-2xl',
            'transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2',
            'focus-visible:outline-offset-2 focus-visible:outline-brand-orange-500',
          )}
        >
          <MessageSquareText size={22} strokeWidth={2} />
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 size-3 rounded-full border-2 border-[rgba(11,18,32,0.98)] bg-emerald-400"
          />
        </button>
      )}
    </div>
  );
}
